import { useContext, useState } from "react";
import { join } from "../util/base.util";
import { AuthContext } from "../contexts/auth.context";
import { ToasterContext } from "../contexts/toaster.context";

type ApiOptions = {
    method: "GET"|"POST"|"PUT"|"PATCH"|"DELETE";
    headers?: Record<string, string>;
    body?: Record<string, any>|FormData;
};

export interface ApiHook<T=any> {
    get: (path?: string) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    post: (path?: string, body?: Record<string, any> | FormData | undefined) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    put: (path?: string, body?: FormData | Record<string, any>) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    patch: (path?: string, body?: FormData | Record<string, any>) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    delete: (path?: string, body?: FormData | Record<string, any>) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    query: (path?: string, options?: ApiOptions) => Promise<{
        ok: boolean;
        status: number;
        statusText: string;
        data: any;
    }>;
    data: T | undefined;
}

export function useApi<T=any>(root: string = ""): ApiHook<T> {
    root = join('/api/v1', root);

    const auth = useContext(AuthContext);
    const toaster = useContext(ToasterContext);

    const [data, setData] = useState<T|undefined>(undefined);

    async function query(path: string = "", options: ApiOptions = {
        method: "GET"
    }) {
        path = join(root, path);
        const headers: Record<string, string> = {};

        if (options.headers) Object.assign(headers, options.headers);

        if (auth.token) {
            headers.authorization = `Bearer ${auth.token}`;
        }

        const body = options.body ? (options.body instanceof FormData ? options.body : JSON.stringify(options.body)) : undefined;

        if (typeof body === 'string') {
            headers['Content-Type'] = 'application/json';
        }

        const res = await fetch(path, {
            method: options.method,
            headers,
            body
        });

        const ctype = res.headers.get("content-type");
        let data: any;

        if (ctype?.startsWith("application/json")) {
            data = await res.json();

            if (!res.ok && data.message) toaster.makeToast(data.message);
        } else {
            data = await res.text();
            if (!res.ok) toaster.makeToast(data);
        }

        return {
            ok: res.ok,
            status: res.status,
            statusText: res.statusText,
            data
        };
    }

    async function get(path: string = "") {
        const res = await query(path);

        if (res.ok && res.data) {
            setData(res.data);
        }

        return res;
    }

    function post(path: string = "", body?: FormData|Record<string, any>) {
        return query(path, {
            method: "POST",
            body
        });
    }

    function put(path: string = "", body?: FormData|Record<string, any>) {
        return query(path, {
            method: "PUT",
            body
        });
    }

    function patch(path: string = "", body?: FormData|Record<string, any>) {
        return query(path, {
            method: "PATCH",
            body
        });
    }

    function remove(path: string = "", body?: FormData|Record<string, any>) {
        return query(path, {
            method: "DELETE",
            body
        });
    }

    return {
        get,
        post,
        put,
        patch,
        delete: remove,
        query,
        data
    };
}